import {
  createEffect,
  createResource,
  createSignal,
  For,
  Show,
  type Component,
} from "solid-js";
import { A } from "@solidjs/router";
import { User, Plus, Save } from "lucide-solid";
import { api, type Snip } from "../lib/api";
import { currentUser, isLoggedIn, refreshCurrentUser } from "../lib/auth";
import SnipCard from "../components/SnipCard";
import Spinner from "../components/Spinner";
import { TextField, TextFieldLabel, TextFieldInput, TextFieldTextArea } from "../components/ui/text-field";
import { Button } from "../components/ui/Button";

async function fetchMySnips(username: string): Promise<Snip[]> {
  const { data, error } = await api.GET("/users/{username}", {
    params: { path: { username } },
  });
  if (error) throw new Error("Profil yüklenemedi");
  return data?.snips ?? [];
}

const Profile: Component = () => {
  const [displayName, setDisplayName] = createSignal("");
  const [bio, setBio] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [saved, setSaved] = createSignal(false);

  // Keep the form synced with the loaded current user.
  createEffect(() => {
    const u = currentUser();
    if (u) {
      setDisplayName(u.displayName ?? "");
      setBio(u.bio ?? "");
    }
  });

  const [snips] = createResource(
    () => currentUser()?.username,
    fetchMySnips,
  );

  const save = async (e: Event) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await api.PUT("/users/me", {
        body: { displayName: displayName(), bio: bio() },
      });
      await refreshCurrentUser();
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="mx-auto max-w-3xl px-4 py-6">
      <div class="mb-5 flex items-center gap-2">
        <User size={20} class="text-primary" />
        <h1 class="m-0 text-lg font-semibold">Profil</h1>
      </div>

      <Show
        when={isLoggedIn()}
        fallback={
          <div class="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
            Profilini görmek için{" "}
            <A href="/sign-in" class="text-primary">giriş yap</A>.
          </div>
        }
      >
        <form
          onSubmit={save}
          class="mb-8 flex flex-col gap-4 rounded-xl border border-border bg-card p-5"
        >
          <div class="text-sm text-muted-foreground">@{currentUser()?.username}</div>
          <TextField value={displayName()} onChange={setDisplayName}>
            <TextFieldLabel>Görünen Ad</TextFieldLabel>
            <TextFieldInput type="text" />
          </TextField>
          <TextField value={bio()} onChange={setBio}>
            <TextFieldLabel>Hakkında</TextFieldLabel>
            <TextFieldTextArea rows={3} />
          </TextField>
          <div class="flex items-center gap-3">
            <Button type="submit" disabled={saving()}>
              <Save size={16} />
              {saving() ? "Kaydediliyor..." : "Kaydet"}
            </Button>
            <Show when={saved()}>
              <span class="text-sm text-primary">Kaydedildi</span>
            </Show>
          </div>
        </form>

        <div class="mb-4 flex items-center justify-between">
          <h2 class="m-0 text-base font-semibold">Gönderilerim</h2>
          <A href="/new">
            <Button variant="secondary">
              <Plus size={16} />
              Yeni Gönderi
            </Button>
          </A>
        </div>

        <Show
          when={!snips.loading}
          fallback={
            <div class="flex justify-center py-12">
              <Spinner size={26} />
            </div>
          }
        >
          <Show
            when={(snips() ?? []).length > 0}
            fallback={
              <div class="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
                Henüz bir gönderi paylaşmadın.
              </div>
            }
          >
            <div class="flex flex-col gap-3">
              <For each={snips()}>{(snip) => <SnipCard snip={snip} />}</For>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
};

export default Profile;
